import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { NetworkIcon } from '@root/entities/network/ui'
import { PairLiquidityFormatted } from '@root/entities/pair/ui/common/pair-liquidity-formatted'
import { PercentChange } from '@root/entities/trend/ui/percent-change'
import {
  AmountFormatted,
  TxCountFormatted,
  VolumeShortFormatted,
} from '@root/shared/core/format'

import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'

const STATS_PERIOD = '1D'

type PairSearchItemMetricsProps = {
  pair: PairModel
} & ClassNameProps

export const PairSearchItemMetrics: React.FC<PairSearchItemMetricsProps> = ({
  pair,
}) => {
  const { t } = useTranslation()

  const [volumeUsd] = useAtom(pair.stats[STATS_PERIOD].volumeUsd)
  const [transactions] = useAtom(pair.stats[STATS_PERIOD].transactions)
  const [totalFees] = useAtom(pair.fees.totalFees)
  const [priceChange] = useAtom(pair.stats[STATS_PERIOD].priceChange)

  return (
    <Root>
      <Stat>
        <Label>F</Label>
        <Value>
          <AmountFormatted amount={totalFees} />
          <NetworkIcon
            size={10}
            network={pair.network}
            color="var(--background-11)"
          />
        </Value>
      </Stat>
      <Stat>
        <Label>{t('common.volume_char')}</Label>
        <Value>
          <VolumeShortFormatted volume={volumeUsd} />
        </Value>
      </Stat>
      <Stat>
        <Label>{t('pool.liquidity-char')}</Label>
        <Value>
          <PairLiquidityFormatted pair={pair} />
        </Value>
      </Stat>
      <Stat>
        <Label>{t('common.txs')}</Label>
        <Value>
          <TxCountFormatted txCount={transactions} />
        </Value>
      </Stat>
      <PriceChangeStyled percents={priceChange} />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid var(--backgroundA-2);
  padding-block: 3px 4px;
  padding-inline: 9px;
`

const Stat = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;
`

const Label = styled(Typography).attrs({ variant: 'mobile-12-medium' })`
  color: var(--text-9);
`

const Value = styled(Typography).attrs({ variant: 'mobile-12-medium' })`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 2px;

  color: var(--text-11);
`

const PriceChangeStyled = styled(PercentChange)`
  /* Mobile/12 Medium */
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`
